import React, { useState, useMemo } from 'react';
import { ShoppingBag, Plus, Trash2, Gift, CheckCircle, Edit2, Calendar, DollarSign } from 'lucide-react';
import { useProfile } from '../ProfileContext';
import SectionWrapper from '../components/SectionWrapper';

const MembershipsSection = () => {
  const {
    memberships,
    activePerks,
    subscriptions,
    refData,
    expandedSections,
    saving,
    addMembership,
    saveMembership,
    updateMembership,
    deleteMembership,
    saveActivePerk,
    toggleSection,
    isSectionDirty,
  } = useProfile();

  const isExpanded = expandedSections.includes('memberships');
  const isDirty = isSectionDirty('memberships');

  const [showAddForm, setShowAddForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [newMembership, setNewMembership] = useState({
    membership_name: '',
    membership_type: '',
    billing_cycle: 'monthly',
    monthly_cost: '',
    annual_cost: '',
    isOther: false,
  });

  // Get membership options from reference data
  const membershipOptions = useMemo(() => {
    const membershipMap = new Map();

    refData.membershipPerks?.forEach(perk => {
      if (!perk.membership_type) return;

      if (!membershipMap.has(perk.membership_type)) {
        membershipMap.set(perk.membership_type, {
          name: perk.membership_name || perk.membership_type,
          type: perk.membership_type,
          perks: [],
        });
      }

      const membership = membershipMap.get(perk.membership_type);

      if (perk.service_name) {
        membership.perks.push({
          service: perk.service_name,
          tier: perk.included_tier,
          isChoice: perk.is_choice,
          choiceGroup: perk.choice_group,
          monthlyValue: parseFloat(perk.monthly_value) || 0,
        });
      }
    });

    if (membershipMap.size > 0) {
      return Array.from(membershipMap.values()).sort((a, b) =>
        a.name.localeCompare(b.name)
      );
    }

    // Fallback
    return [
      { name: 'Amazon Prime', type: 'amazon_prime', perks: [{ service: 'Prime Video', tier: 'Included', isChoice: false, monthlyValue: 8.99 }] },
      { name: 'Walmart+', type: 'walmart_plus', perks: [{ service: 'Paramount+', tier: 'Essential', isChoice: true, monthlyValue: 7.99 }, { service: 'Peacock', tier: 'Premium', isChoice: true, monthlyValue: 7.99 }] },
      { name: 'Costco', type: 'costco', perks: [] },
      { name: "Sam's Club", type: 'sams_club', perks: [] },
    ];
  }, [refData.membershipPerks]);

  // Get ACTIVE perks for a membership (what user actually selected)
  const getActiveMembershipPerks = (membershipType, membershipName) => {
    if (!activePerks) return [];
    
    const membershipLower = membershipType.toLowerCase();
    const nameLower = membershipName?.toLowerCase() || '';
    
    return activePerks.filter(perk => {
      const source = perk.perk_source?.toLowerCase() || '';
      return source.includes(membershipLower) || 
             source.includes(nameLower.replace(/\s+/g, '_')) ||
             source.includes(nameLower.split(' ')[0]);
    });
  };

  // Get perks from subscriptions that are marked as membership benefits
  const getSubscriptionMembershipPerks = (membershipType) => {
    if (!subscriptions) return [];
    
    return subscriptions.filter(sub => 
      sub.payment_method === 'membership_benefit' && 
      sub.payment_source === membershipType
    );
  };

  // Get reference perks for a membership (what's possible)
  const getReferenceMembershipPerks = (membershipType) => {
    return refData.membershipPerks?.filter(p => p.membership_type === membershipType) || [];
  };

  // Calculate monthly equivalent cost for a membership
  const getMonthlyEquivalent = (membership) => {
    if (membership.annual_cost && membership.annual_cost > 0) {
      return membership.annual_cost / 12;
    }
    return membership.monthly_cost || 0;
  };

  // Calculate streaming value from a membership
  const getStreamingValue = (membership) => {
    const referencePerks = getReferenceMembershipPerks(membership.membership_type);
    const activePerksForMembership = getActiveMembershipPerks(membership.membership_type, membership.membership_name);
    
    let totalValue = 0;
    
    // For choice perks, only count the selected one
    const hasChoicePerks = referencePerks.some(p => p.is_choice);
    
    if (hasChoicePerks) {
      // Find the active choice perk
      const activePerk = activePerksForMembership[0];
      if (activePerk) {
        const refPerk = referencePerks.find(p => p.service_name === activePerk.service_name);
        totalValue = parseFloat(refPerk?.monthly_value) || 0;
      }
    } else {
      // Sum all non-choice perks
      referencePerks.forEach(perk => {
        totalValue += parseFloat(perk.monthly_value) || 0;
      });
    }
    
    return totalValue;
  };

  const handleMembershipSelect = (e) => {
    const selectedType = e.target.value;

    if (selectedType === 'other') {
      setNewMembership({
        membership_name: '',
        membership_type: 'other',
        billing_cycle: 'monthly',
        monthly_cost: '',
        annual_cost: '',
        isOther: true,
      });
    } else {
      const selected = membershipOptions.find(m => m.type === selectedType);
      setNewMembership({
        membership_name: selected?.name || '',
        membership_type: selectedType,
        billing_cycle: 'monthly',
        monthly_cost: '',
        annual_cost: '',
        isOther: false,
      });
    }
  };

  const handleAddMembership = async () => {
    if (!newMembership.membership_name || !newMembership.membership_type) return;

    const membership = addMembership({
      membership_name: newMembership.membership_name,
      membership_type: newMembership.membership_type,
      monthly_cost: newMembership.billing_cycle === 'monthly' ? parseFloat(newMembership.monthly_cost) || 0 : 0,
      annual_cost: newMembership.billing_cycle === 'annual' ? parseFloat(newMembership.annual_cost) || 0 : 0,
    });

    await saveMembership(membership);

    setNewMembership({
      membership_name: '',
      membership_type: '',
      billing_cycle: 'monthly',
      monthly_cost: '',
      annual_cost: '',
      isOther: false,
    });
    setShowAddForm(false);
  };

  const handleRemoveMembership = async (membershipId) => {
    if (window.confirm('Remove this membership? Any associated perks will be affected.')) {
      await deleteMembership(membershipId);
    }
  };

  const handleSaveMembershipCost = async (membership, updates) => {
    const updatedMembership = { ...membership, ...updates };
    if (updateMembership) {
      updateMembership(updatedMembership);
    }
    await saveMembership(updatedMembership);
    setEditingId(null);
  };

  // Calculate total monthly cost across all memberships
  const totalMonthlyCost = memberships.reduce((total, m) => {
    return total + getMonthlyEquivalent(m);
  }, 0);

  // Calculate total streaming value
  const totalStreamingValue = memberships.reduce((total, m) => {
    return total + getStreamingValue(m);
  }, 0);

  const summary = memberships.length > 0
    ? `${memberships.length} membership${memberships.length > 1 ? 's' : ''} • $${totalMonthlyCost.toFixed(2)}/mo`
    : 'No memberships added';

  return (
    <SectionWrapper
      title="Memberships"
      icon={ShoppingBag}
      isExpanded={isExpanded}
      onToggle={() => toggleSection('memberships')}
      isDirty={isDirty}
      isSaving={saving}
      summary={summary}
      badge={memberships.length > 0 ? memberships.length : null}
    >
      <div className="space-y-4">
        {/* Existing Memberships */}
        {memberships.map(membership => {
          const activePerksForMembership = getActiveMembershipPerks(membership.membership_type, membership.membership_name);
          const subscriptionPerks = getSubscriptionMembershipPerks(membership.membership_type);
          const referencePerks = getReferenceMembershipPerks(membership.membership_type);
          const optionInfo = membershipOptions.find(m => m.type === membership.membership_type);
          const isEditing = editingId === membership.id;
          const monthlyEquivalent = getMonthlyEquivalent(membership);
          const streamingValue = getStreamingValue(membership);
          const isAnnual = membership.annual_cost > 0;
          
          // Combine active perks from both sources
          const displayPerks = [];
          
          // Add perks from active_perks table
          activePerksForMembership.forEach(perk => {
            if (!displayPerks.some(p => p.service === perk.service_name)) {
              const refPerk = referencePerks.find(p => p.service_name === perk.service_name);
              displayPerks.push({
                service: perk.service_name,
                tier: perk.tier,
                isActive: true,
                monthlyValue: parseFloat(refPerk?.monthly_value) || 0,
              });
            }
          });
          
          // Add perks from subscriptions marked as membership benefits
          subscriptionPerks.forEach(sub => {
            if (!displayPerks.some(p => p.service === sub.service_name)) {
              const refPerk = referencePerks.find(p => p.service_name === sub.service_name);
              displayPerks.push({
                service: sub.service_name,
                tier: sub.subscription_tier,
                isActive: true,
                monthlyValue: parseFloat(refPerk?.monthly_value) || 0,
              });
            }
          });

          // Check if this membership has choice perks (like Walmart+)
          const hasChoicePerks = referencePerks.some(p => p.is_choice);
          const choicePerksAvailable = referencePerks.filter(p => p.is_choice);

          if (isEditing) {
            return (
              <MembershipEditForm
                key={membership.id}
                membership={membership}
                onSave={(updates) => handleSaveMembershipCost(membership, updates)}
                onCancel={() => setEditingId(null)}
              />
            );
          }

          return (
            <div key={membership.id} className="border border-gray-200 rounded-lg p-4">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium text-gray-900">{membership.membership_name}</span>
                    {isAnnual && (
                      <span className="px-2 py-0.5 text-xs bg-blue-100 text-blue-700 rounded flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        Annual
                      </span>
                    )}
                  </div>
                  
                  {/* Cost display */}
                  {monthlyEquivalent > 0 ? (
                    <div className="mt-1 text-sm text-gray-600">
                      {isAnnual ? (
                        <>
                          <span className="font-medium">${membership.annual_cost.toFixed(2)}/yr</span>
                          <span className="text-gray-400 ml-1">(≈ ${monthlyEquivalent.toFixed(2)}/mo)</span>
                        </>
                      ) : (
                        <span className="font-medium">${monthlyEquivalent.toFixed(2)}/mo</span>
                      )}
                      {streamingValue > 0 && (
                        <span className="text-green-600 ml-2">
                          • Streaming value: ${streamingValue.toFixed(2)}/mo
                        </span>
                      )}
                    </div>
                  ) : (
                    <button
                      onClick={() => setEditingId(membership.id)}
                      className="mt-1 text-sm text-purple-600 hover:text-purple-800 flex items-center gap-1"
                    >
                      <DollarSign className="w-3 h-3" />
                      Add cost info
                    </button>
                  )}
                  
                  {/* Show ACTIVE perks (what user is actually using) */}
                  {displayPerks.length > 0 && (
                    <div className="mt-2 space-y-1">
                      {displayPerks.map((perk, index) => (
                        <div key={index} className="flex items-center gap-2 text-sm text-green-600">
                          <CheckCircle className="w-3 h-3" />
                          <span>
                            {perk.service}
                            {perk.tier && <span className="text-green-500 ml-1">({perk.tier})</span>}
                          </span>
                          <span className="text-xs bg-green-100 text-green-700 px-1.5 py-0.5 rounded">Included</span>
                          {perk.monthlyValue > 0 && (
                            <span className="text-xs text-gray-400">${perk.monthlyValue.toFixed(2)}/mo value</span>
                          )}
                        </div>
                      ))}
                      
                      {/* If this is a choice perk, show option to change */}
                      {hasChoicePerks && choicePerksAvailable.length > 1 && (
                        <div className="mt-2 pt-2 border-t border-gray-200">
                          <p className="text-xs text-gray-500 mb-1">Switch to:</p>
                          <div className="flex flex-wrap gap-2">
                            {choicePerksAvailable
                              .filter(p => !displayPerks.some(dp => dp.service === p.service_name))
                              .map((perk, index) => (
                                <button
                                  key={index}
                                  onClick={() => saveActivePerk({
                                    perk_source: membership.membership_type,
                                    service_name: perk.service_name,
                                    tier: perk.included_tier,
                                    choice_group: perk.choice_group,
                                  })}
                                  className="text-xs px-2 py-1 bg-gray-100 hover:bg-purple-100 text-gray-600 hover:text-purple-700 rounded transition-colors"
                                >
                                  {perk.service_name}
                                </button>
                              ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* If membership has choice perks but none selected, show selector */}
                  {hasChoicePerks && displayPerks.length === 0 && (
                    <div className="mt-2 p-3 bg-yellow-50 border border-yellow-200 rounded">
                      <p className="text-yellow-800 font-medium text-sm mb-2">Choose your streaming perk:</p>
                      <div className="space-y-2">
                        {choicePerksAvailable.map((perk, index) => (
                          <button
                            key={index}
                            onClick={() => saveActivePerk({
                              perk_source: membership.membership_type,
                              service_name: perk.service_name,
                              tier: perk.included_tier,
                              choice_group: perk.choice_group,
                            })}
                            className="w-full flex items-center justify-between p-2 bg-white border border-yellow-300 rounded hover:bg-yellow-100 transition-colors text-left"
                          >
                            <span className="text-gray-900">
                              {perk.service_name}
                              {perk.included_tier && <span className="text-gray-500 ml-1">({perk.included_tier})</span>}
                            </span>
                            <span className="text-xs text-purple-600 font-medium">Select</span>
                          </button>
                        ))}
                      </div>
                      <p className="text-yellow-600 text-xs mt-2 italic">
                        You can change this later (Walmart+ allows switching every 90 days)
                      </p>
                    </div>
                  )}

                  {/* For non-choice perks like Amazon Prime, show if not activated */}
                  {!hasChoicePerks && displayPerks.length === 0 && referencePerks.length > 0 && (
                    <div className="mt-2 space-y-1">
                      {referencePerks.map((perk, index) => (
                        <div key={index} className="flex items-center gap-2 text-sm text-gray-500">
                          <Gift className="w-3 h-3" />
                          <span>Includes {perk.service_name}</span>
                          {perk.included_tier && <span className="text-gray-400">({perk.included_tier})</span>}
                          {perk.monthly_value > 0 && (
                            <span className="text-xs text-gray-400">${parseFloat(perk.monthly_value).toFixed(2)}/mo value</span>
                          )}
                        </div>
                      ))}
                    </div>
                  )}

                  {/* No perks available */}
                  {referencePerks.length === 0 && displayPerks.length === 0 && (
                    <p className="mt-1 text-xs text-gray-500">No streaming perks associated</p>
                  )}
                </div>

                <div className="flex items-center gap-1">
                  <button
                    onClick={() => setEditingId(membership.id)}
                    className="p-2 text-gray-400 hover:text-purple-600 hover:bg-purple-50 rounded-lg transition-colors"
                  >
                    <Edit2 className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleRemoveMembership(membership.id)}
                    className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          );
        })}

        {/* Empty State */}
        {memberships.length === 0 && !showAddForm && (
          <div className="text-center py-6 text-gray-500">
            <ShoppingBag className="w-12 h-12 mx-auto mb-3 text-gray-300" />
            <p className="text-sm">No memberships added yet</p>
            <p className="text-xs mt-1">Add Amazon Prime, Walmart+, etc. to discover included streaming</p>
          </div>
        )}

        {/* Add Membership Form */}
        {showAddForm && (
          <div className="border-2 border-purple-200 rounded-lg p-4 space-y-4">
            <h4 className="font-medium text-gray-900">Add Membership</h4>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Select Membership
              </label>
              <select
                value={newMembership.isOther ? 'other' : newMembership.membership_type}
                onChange={handleMembershipSelect}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
              >
                <option value="">Choose a membership...</option>
                {membershipOptions.map(membership => (
                  <option key={membership.type} value={membership.type}>
                    {membership.name}
                    {membership.perks.length > 0 && membership.perks[0].isChoice 
                      ? ` - Choice: ${membership.perks.map(p => p.service).join(' or ')}`
                      : membership.perks.length > 0 
                        ? ` - Includes ${membership.perks[0].service}`
                        : ''
                    }
                  </option>
                ))}
                <option value="other">Other</option>
              </select>
            </div>

            {newMembership.isOther && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Membership Name
                </label>
                <input
                  type="text"
                  value={newMembership.membership_name}
                  onChange={(e) => setNewMembership({ ...newMembership, membership_name: e.target.value })}
                  placeholder="Enter membership name"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
              </div>
            )}

            {/* Cost input */}
            {newMembership.membership_type && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Billing Cycle
                  </label>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => setNewMembership({ ...newMembership, billing_cycle: 'monthly' })}
                      className={`flex-1 px-4 py-2 rounded-lg font-medium transition-colors ${
                        newMembership.billing_cycle === 'monthly'
                          ? 'bg-purple-600 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      Monthly
                    </button>
                    <button
                      type="button"
                      onClick={() => setNewMembership({ ...newMembership, billing_cycle: 'annual' })}
                      className={`flex-1 px-4 py-2 rounded-lg font-medium transition-colors ${
                        newMembership.billing_cycle === 'annual'
                          ? 'bg-purple-600 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      Annual
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {newMembership.billing_cycle === 'annual' ? 'Annual' : 'Monthly'} Cost ($)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={newMembership.billing_cycle === 'annual' ? newMembership.annual_cost : newMembership.monthly_cost}
                    onChange={(e) => {
                      if (newMembership.billing_cycle === 'annual') {
                        setNewMembership({ ...newMembership, annual_cost: e.target.value });
                      } else {
                        setNewMembership({ ...newMembership, monthly_cost: e.target.value });
                      }
                    }}
                    placeholder={newMembership.billing_cycle === 'annual' ? "139.00" : "14.99"}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                  />
                  {newMembership.billing_cycle === 'annual' && newMembership.annual_cost && (
                    <p className="text-xs text-gray-500 mt-1">
                      ≈ ${(parseFloat(newMembership.annual_cost) / 12).toFixed(2)}/month
                    </p>
                  )}
                </div>
              </>
            )}

            {/* Show selected membership perks info */}
            {newMembership.membership_type && !newMembership.isOther && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                <p className="text-sm font-medium text-green-800 mb-2">Streaming perks:</p>
                {membershipOptions
                  .find(m => m.type === newMembership.membership_type)
                  ?.perks.map((perk, index) => (
                    <div key={index} className="flex items-center gap-2 text-sm text-green-700">
                      <Gift className="w-3 h-3" />
                      <span>
                        {perk.isChoice ? `Choice: ${perk.service}` : `Includes ${perk.service}`}
                        {perk.tier && <span className="text-green-600 ml-1">({perk.tier})</span>}
                      </span>
                      {perk.monthlyValue > 0 && (
                        <span className="text-xs text-green-500">${perk.monthlyValue.toFixed(2)}/mo value</span>
                      )}
                    </div>
                  )) || (
                  <p className="text-sm text-green-600 italic">No streaming perks associated</p>
                )}
              </div>
            )}

            <div className="flex gap-3">
              <button
                onClick={() => setShowAddForm(false)}
                className="flex-1 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleAddMembership}
                disabled={!newMembership.membership_name || !newMembership.membership_type}
                className="flex-1 px-4 py-2 bg-purple-600 hover:bg-purple-700 disabled:bg-gray-300 text-white font-medium rounded-lg transition-colors"
              >
                Add Membership
              </button>
            </div>
          </div>
        )}

        {/* Add Button */}
        {!showAddForm && (
          <button
            onClick={() => setShowAddForm(true)}
            className="w-full flex items-center justify-center gap-2 px-4 py-3 border-2 border-dashed border-gray-300 hover:border-purple-400 text-gray-600 hover:text-purple-600 rounded-lg transition-colors"
          >
            <Plus className="w-5 h-5" />
            Add Membership
          </button>
        )}

        {/* Summary */}
        {memberships.length > 0 && (
          <div className="mt-4 pt-4 border-t border-gray-200">
            <div className="flex justify-between items-center text-sm">
              <span className="text-gray-600">Total membership cost:</span>
              <span className="font-medium text-gray-900">${totalMonthlyCost.toFixed(2)}/mo</span>
            </div>
            {totalStreamingValue > 0 && (
              <div className="flex justify-between items-center text-sm mt-1">
                <span className="text-gray-600">Streaming value included:</span>
                <span className="font-medium text-green-600">${totalStreamingValue.toFixed(2)}/mo</span>
              </div>
            )}
          </div>
        )}
      </div>
    </SectionWrapper>
  );
};

// Membership Edit Form Component
const MembershipEditForm = ({ membership, onSave, onCancel }) => {
  const isAnnual = membership.annual_cost > 0;
  const [formData, setFormData] = useState({
    billing_cycle: isAnnual ? 'annual' : 'monthly',
    monthly_cost: membership.monthly_cost || '',
    annual_cost: membership.annual_cost || '',
  });

  const handleSave = () => {
    const updates = {
      monthly_cost: formData.billing_cycle === 'monthly' ? parseFloat(formData.monthly_cost) || 0 : 0,
      annual_cost: formData.billing_cycle === 'annual' ? parseFloat(formData.annual_cost) || 0 : 0,
    };
    onSave(updates);
  };

  return (
    <div className="border-2 border-purple-300 rounded-lg p-4 bg-purple-50">
      <h4 className="font-semibold text-gray-900 mb-4">{membership.membership_name}</h4>

      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Billing Cycle
          </label>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setFormData({ ...formData, billing_cycle: 'monthly' })}
              className={`flex-1 px-4 py-2 rounded-lg font-medium transition-colors ${
                formData.billing_cycle === 'monthly'
                  ? 'bg-purple-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Monthly
            </button>
            <button
              type="button"
              onClick={() => setFormData({ ...formData, billing_cycle: 'annual' })}
              className={`flex-1 px-4 py-2 rounded-lg font-medium transition-colors ${
                formData.billing_cycle === 'annual'
                  ? 'bg-purple-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Annual
            </button>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {formData.billing_cycle === 'annual' ? 'Annual' : 'Monthly'} Cost ($)
          </label>
          <input
            type="number"
            step="0.01"
            min="0"
            value={formData.billing_cycle === 'annual' ? formData.annual_cost : formData.monthly_cost}
            onChange={(e) => {
              if (formData.billing_cycle === 'annual') {
                setFormData({ ...formData, annual_cost: e.target.value });
              } else {
                setFormData({ ...formData, monthly_cost: e.target.value });
              }
            }}
            placeholder={formData.billing_cycle === 'annual' ? "139.00" : "14.99"}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
          />
          {formData.billing_cycle === 'annual' && formData.annual_cost && (
            <p className="text-xs text-gray-500 mt-1">
              ≈ ${(parseFloat(formData.annual_cost) / 12).toFixed(2)}/month
            </p>
          )}
        </div>

        <p className="text-xs text-gray-500">
          Include taxes if you want accurate cost tracking
        </p>

        <div className="flex gap-3 pt-2">
          <button
            onClick={onCancel}
            className="flex-1 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-lg transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            className="flex-1 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white font-medium rounded-lg transition-colors"
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );
};

export default MembershipsSection;